/**
 * Export Jurisdiction and Classification U.S. EAR99, India Not Subject to Regulations
 */

import { AttributeInputCallback, CallbackType, Config, FRAuth, FRCallback, FRLoginFailure, FRLoginSuccess, StepType, type ConfigOptions, type FRStep } from "@forgerock/javascript-sdk";
import { cloneDeep, isNil } from "lodash";
import { useEffect } from "react";
import { MessageType, type gotoAndResumeSetter, type HandleFailureStepParams } from "../interfaces/baseInterface";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../components/common/Translation/translationProvider";
import { useAppDispatch } from "../redux/hooks";
import { setPortalName } from "../redux/loginSlice";
import { ciam_trees, configOptionsCIAM, configOptionsPWC, trees } from "../config/forgerockConfig";
import type { StringDict } from "@forgerock/javascript-sdk/src/shared/interfaces";


const typeBasedCallbacks = [
  CallbackType.NameCallback,
  CallbackType.PasswordCallback
]
/**
 * Clean the data before submitting to FRAuth.next().
 * @param {object} data The data to be cleaned.
 * @returns {object} The cleaned data.
 */
export const cleanDataBeforeSubmit = (data: any) => {
  for (let key in data) {
    if (data[key] === "" || typeof data[key] === "undefined") {
      data[key] = "";
    } else if (typeof data[key] === "string") {
      data[key] = data[key].trim();
    } else if (typeof data[key] === "object") {
      data[key] = cleanDataBeforeSubmit(data[key]);
    }
  }
  return data;
};


/**
 * Converts a given value to a boolean if it is a string representation of a boolean value.
 * It does not convert non-string values, instead returning the original value.
 * @param {any} value the value to be converted
 * @returns {any} the converted value, or the original value if it was not a string
 */
var str2bool = (value: any) => {
  if (value && typeof value === "string") {
    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;
  }
  return value;
};


/**
 * Fill the callbacks of a given step with the data from a given object.
 * @param {FRStep} step The step to be filled.
 * @param {object} data The data to be used for filling the callbacks.
 * @param {Map<String, any>} metaDataMap A map of metadata for the step, such as the type of callbacks and their names.
 * @returns {FRStep} The filled step.
 */
export const fillStep = (step: FRStep, data: any, metaDataMap: any): FRStep => {
  const fillStep = step;
  data = cleanDataBeforeSubmit(data)
  if (metaDataMap.get("typeBasedCallbacks")) {
    typeBasedCallbacks.forEach(typeBasedCallback => {
      fillStep.getCallbacksOfType(typeBasedCallback).forEach(callback => {
        if (typeof data[metaDataMap.get("typeBasedCallbacks")[typeBasedCallback].name] === "string") {
          callback.setInputValue(data[metaDataMap.get("typeBasedCallbacks")[typeBasedCallback].name]);
        } else {
          callback.setInputValue(JSON.stringify(data[metaDataMap.get("typeBasedCallbacks")[typeBasedCallback].name]));
        }
      })
    })
  }


  fillStep.getCallbacksOfType(CallbackType.StringAttributeInputCallback).forEach((callback) => {
    const attibuteInputCallback = new AttributeInputCallback(callback.payload);
    if (typeof data[attibuteInputCallback.getName()] === "string") {
      callback.setInputValue(data[attibuteInputCallback.getName()]);
    } else {
      callback.setInputValue(JSON.stringify(data[attibuteInputCallback.getName()]));
    }
  });
  fillStep.getCallbacksOfType(CallbackType.BooleanAttributeInputCallback).forEach((callback) => {
    const attibuteInputCallback = new AttributeInputCallback(callback.payload);
    if (typeof data[attibuteInputCallback.getName()] !== "boolean") {
      callback.setInputValue(str2bool(data[attibuteInputCallback.getName()]));
    } else {
      callback.setInputValue(data[attibuteInputCallback.getName()]);
    }
  });
  fillStep.getCallbacksOfType(CallbackType.ValidatedCreatePasswordCallback).forEach((callback) => {
    const attibuteInputCallback = new AttributeInputCallback(callback.payload);
    callback.setInputValue(getValueByPath(data, attibuteInputCallback.getPrompt()) || data[attibuteInputCallback.getPrompt()]);
  });
  fillStep.getCallbacksOfType(CallbackType.TermsAndConditionsCallback).forEach((callback) => {
    //  const attibuteInputCallback = new AttributeInputCallback(callback.payload);
    callback.setInputValue(data["termsAndConditions"]);
  });

  return fillStep;
}

/**
 * Given a step and a message type, returns the text output callback of the step
 * that has the given message type. If no such callback exists, returns undefined.
 * @param {FRStep} step The step to search for the callback.
 * @param {string} messageType The message type of the callback to search for.
 * @returns {FRCallback} The text output callback with the given message type, or undefined if none exists.
 */
export const getTextOutputCallBackByMessageType = (step: FRStep, messageType: string): FRCallback => {
  let returnCallback: any
  const textOutputCallbacks: FRCallback[] = step.getCallbacksOfType(CallbackType.TextOutputCallback)
  if (textOutputCallbacks.length) {
    textOutputCallbacks.forEach((callback: FRCallback) => {
      if (callback.getOutputByName('messageType', '') === messageType)
        returnCallback = callback;
      return;
    })
  }
  return returnCallback;
}

/**
 * Given an object and a string path, returns the value of the property at that
 * path in the object. If no such property exists, returns undefined.
 * @param {object} object The object to search for the property.
 * @param {string} path The path of the property to search for.
 * @returns {any} The value of the property, or undefined if the property does
 * not exist.
 */
export const getValueByPath = (object: any, path: string) => {
  return path.split('.').reduce((acc, key) => acc && acc[key], object)
}

/**
 * Returns true if the given string is a valid JSON string, and false otherwise.
 * @param {string} str The string to check.
 * @returns {boolean} True if the string is valid JSON, and false otherwise.
 */
export const isJsonString = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}
/**
 * Handles a failed step in the FRAuth flow. If restartOnFailure is true and
 * tree is defined, the flow will be restarted with the given tree and query
 * parameters. The message and message type of the failed step will be set in
 * the given setMessage and setMessageType functions, respectively. If no such
 * functions are given, the message will be logged to the console.
 * @param {HandleFailureStepParams} params - An object containing the failed
 * step, functions to set the step, alert open, message, and message type, and
 * the tree and query parameters to restart the flow with.
 */
export const handleFailureStep = async (params: HandleFailureStepParams) => {
  const {
    failStep,
    setStep,
    setIsAlertOpen,
    setMessageType,
    setMessage,
    tree,
    restartOnFailure = false,
    queryParams
    
  } = params;
  console.log('step', failStep);
   const portal = new URLSearchParams(location.search).get('portal') ?? '';
   let message = failStep?.payload?.message || '';
   if (message.toLowerCase() =='login failure'){
    message = 'common.error';
   }


  if (restartOnFailure && tree) {
    let query:StringDict<string>={};
    if (!isNil(queryParams)){ 
            queryParams.forEach((value, key) => {
              query[key] = value
            })
          }
    const initialStep = await FRAuth.start({ tree, query:{portal,...query} });
    if (initialStep && initialStep.type === StepType.Step) {
      setStep(initialStep);
    }
  }

  setMessageType ? setMessageType(MessageType.error) : null;
  setMessage ? setMessage(message) : null
  setIsAlertOpen ? setIsAlertOpen(true) : null

};


export const handlePortalName=()=>{
  const navigate= useNavigate();
  const dispatch = useAppDispatch();
  var forgerockConfigOptions = configOptionsPWC;
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const portalParam = searchParams.get('portal');
   
    switch (portalParam?.toLowerCase()) {
      case 'supplier':
        
        dispatch(setPortalName("main.portalName.supplier"));
        break;
      case 'customer':
        dispatch(setPortalName('main.portalName.customer'));
        break; 
      case 'ciam':
        dispatch(setPortalName('RTX Portal Login'));
        forgerockConfigOptions=configOptionsCIAM;
        break;  
    }
     Config.set(forgerockConfigOptions as ConfigOptions);
  })
}

export const checkAndValidatePortalParam=()=>{
  const navigate= useNavigate();
  useEffect(() => {
  const allowedPortals = import .meta.env.VITE_ALLOWED_PORTALS.split(',');
  const searchParams = new URLSearchParams(window.location.search);
  const portalParam = searchParams.get('portal');
  if (!portalParam || !allowedPortals.includes(portalParam?.toLowerCase() ?? '')) {
    navigate('/pageNotFound', { replace: true });
  } 
  },[navigate,location])
 
}

export const getTree=(journeyName: string):string=> {
    const searchParams = new URLSearchParams(window.location.search);
    const portalParam = searchParams.get('portal');
   let tree:string='';
    switch (portalParam?.toLowerCase()) {
      case 'supplier':
      case 'customer':
       tree= trees[journeyName]
        break; 
      case 'ciam':
       tree=ciam_trees[journeyName]
        break;  
    }
   return tree;  
}
 export const redirectionUrl  = (portalParam: string): string => {
  const portalUrls: Record<string, string> = {
    customer: import.meta.env.VITE_PWC_CUSTOMER_URL,
    supplier: import.meta.env.VITE_PWC_SUPPLIER_URL,
    poland: import.meta.env.VITE_PW_POLAND_URL,
    ciam: import.meta.env.VITE_CIAM_URL,
    clrs: import.meta.env.VITE_CLRS_URL,
  };
  
  return portalUrls[portalParam] || window.location.origin;
};

/**
 * gotoAndResumeUseEffect is a hook that is used to resume the FRAuth flow.
 * It is called when the user is redirected back to the original page.
 * It will check if there is a step stored in session storage, if not it will
 * start the flow. If there is a step it will resume the flow.
 * If there is a code and state present in the query params it will resume the flow with the code and state.
 * If there is no code and state present it will throw an error.
 * @param {{setStep: (step: FRStep) => void, setGoto: (goto: string) => void, setRedirectURI: (redirectUri: string) => void, setMaintenancePage: (isMaintenancePage: boolean) => void, tree: string}} gotoAndResumeSetter
 * @returns {void}
 */
export const gotoAndResumeUseEffect = ({ setStep, setGoto, setRedirectURI, setMaintenancePage, tree,queryParams}: gotoAndResumeSetter) => {
  useEffect(() => {
    (async function getStep() {
      console.log("goto triggered")
      const searchParams = new URLSearchParams(window.location.search);
      const portal = searchParams.get('portal') ?? '';
     const code = sessionStorage.getItem('mfacode');
      const state = sessionStorage.getItem('mfastate');
      const goTo = searchParams.get('goto') ?? ''
      setGoto(goTo)


      const stepString = sessionStorage.getItem('step')

      let step;
      if (!!stepString) {
        step = JSON.parse(stepString)?.payload;
      }

      if (!!!step || !code || !state) {
        try {
          console.log('start flow');
          let query:StringDict<string>={};
          let resumeCode = queryParams?.get('resumeCode')

          if (!isNil(queryParams)){ 
            queryParams.forEach((value, key) => {
              query[key] = value
            })
          }
          const initialStep = await FRAuth.start({ tree, query: {portal,goTo, ...query} });
          setStep(initialStep);
        } catch (err) {
          setMaintenancePage ? setMaintenancePage(true) : null;
          console.error(`Error: request for initial step; ${err}`);
        }
      } else {
        if (!!code && !!state) {
          let queryParam;
          if (!isNil(goTo)) {
            queryParam = `&code=${code}&state=${state}&authIndexValue=${tree}`
          } else {
            queryParam = `?code=${code}&state=${state}&authIndexValue=${tree}`
          }
          console.log('resume flow');
          try {
            const nextStep = await FRAuth.resume(window.location.origin+window.location.pathname+'?code='+code+'&state='+state+'&authIndexValue='+tree )
            setStep(nextStep);
          } catch (err) {
            console.error(`Error: request for resume step; ${err}`);
          }
        } else {
          console.error("No code and state present to resume")
        }

      }

    })();
  }, [])
}



export const redirectHandle = (initialStep: FRStep | FRLoginFailure | FRLoginSuccess,tree: string) => {
    if (initialStep && initialStep.type === StepType.Step) {
      let userName = ''
      const meta = initialStep.getCallbacksOfType(CallbackType.MetadataCallback)
      if (!!meta && meta.length) {
        userName = (meta[0].getOutputValue('data') as any)['userName'] as string;
      }
      initialStep.callbacks.forEach((callback, i) => {
        const type = callback.getType()
        if (type === CallbackType.RedirectCallback) {

          if (callback.getOutputValue('trackingCookie')) {
            // save current step information for later resumption of tree.
            sessionStorage.setItem('authIndexValue', tree);
            sessionStorage.setItem('step', JSON.stringify(initialStep));
            sessionStorage.setItem('realm', 'PnW');
            localStorage.setItem(FRAuth.previousStepKey, JSON.stringify(initialStep))
          }
          const redirectUrl = callback.getOutputValue('redirectUrl') as string;
          if (callback.getOutputValue('redirectMethod') === 'POST') {
            const redirectData = callback.getOutputValue('redirectData') as string;
            const form = document.createElement('form');
            form.method = 'post';
            //form.action = redirectUrl+'&login_hint='+email ;
            form.action = redirectUrl;
            Object.entries(redirectData as string).forEach(([key, value]) => {
              const hiddenField = document.createElement('input');
              hiddenField.type = 'hidden';
              hiddenField.name = key;
              hiddenField.value = value;
              form.appendChild(hiddenField);
            });
            const loginHint = document.createElement('input');
            loginHint.type = 'hidden';
            loginHint.name = "login_hint";
            loginHint.value = userName;
            form.appendChild(loginHint);
            document.body.appendChild(form);
            form.submit();
          } else {
            let loginHintRedirectURI = redirectUrl + '&login_hint=' + userName
            let loginHintUrlStep = cloneDeep(initialStep);

            loginHintUrlStep.callbacks.map((callback) => {
              if (callback.getType() === CallbackType.RedirectCallback) {
                callback.payload.output.map((values) => {
                  if (values.name == "redirectUrl") {
                    values.value = loginHintRedirectURI;
                  }
                });
              }
            });
            FRAuth.redirect(loginHintUrlStep as FRStep);
          }
          return;
        }
      })
    }
  }


  export const clearBrowserData = () => {
  // 🔹 Clear all cookies (except HttpOnly)
  document.cookie.split(";").forEach(cookie => {
    const name = cookie.split("=")[0].trim();
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  });

  // 🔹 Clear session storage
  sessionStorage.clear();

  // 🔹 Optional: Clear local storage too
  localStorage.clear();

  console.log("Cookies, sessionStorage, and localStorage cleared.");
};
